<template>
  <div class="reserve-confirm-container">
    <el-row :gutter="16">
      <!-- 左侧核销工作台：扫码/输码 + 核销结果，保留原有协议解析与提交逻辑 -->
      <el-col :xs="24" :sm="24" :md="7" :lg="6">
        <div class="ds-confirm-card">
          <div class="ds-confirm-card__header">
            <i class="el-icon-scan"></i>
            <span>{{ $t('reserveConfirm.confirmPanelTitle') }}</span>
          </div>

          <div class="ds-confirm-card__body">
            <el-input v-model="reserveConfirmInfo.timeId" :placeholder="$t('reserveConfirm.scanPlaceholder')"
              clearable @keyup.enter.native="confirmReserve">
              <el-button slot="append" type="primary" icon="el-icon-check" @click="confirmReserve">
                {{ $t('reserveConfirm.confirm') }}
              </el-button>
            </el-input>

            <div class="ds-confirm-card__tip">
              <i class="el-icon-info"></i>
              <span>{{ $t('reserveConfirm.scanTip') }}</span>
            </div>

            <!-- 核销结果：仅在有结果时展示 -->
            <div class="ds-result" :class="{ 'is-empty': !hasConfirmResult }">
              <div class="ds-result__title">{{ $t('reserveConfirm.result') }}</div>
              <div class="ds-result__list">
                <div class="ds-result__item">
                  <span class="ds-result__label">{{ $t('reserveConfirm.result') }}</span>
                  <span class="ds-result__value">{{ reserveConfirmInfo.order.remark || '-' }}</span>
                </div>
                <div class="ds-result__item">
                  <span class="ds-result__label">{{ $t('reserveConfirm.confirmTime') }}</span>
                  <span class="ds-result__value">{{ reserveConfirmInfo.order.createTime || '-' }}</span>
                </div>
                <div class="ds-result__item">
                  <span class="ds-result__label">{{ $t('reserveConfirm.goodsService') }}</span>
                  <span class="ds-result__value">{{ reserveConfirmInfo.order.goodsName || '-' }}</span>
                </div>
                <div class="ds-result__item">
                  <span class="ds-result__label">{{ $t('reserveConfirm.quantity') }}</span>
                  <span class="ds-result__value ds-num">{{ reserveConfirmInfo.order.quantity || '-' }}</span>
                </div>
                <div class="ds-result__item">
                  <span class="ds-result__label">{{ $t('reserveConfirm.appointmentDate') }}</span>
                  <span class="ds-result__value">{{ reserveConfirmInfo.order.appointmentTime || '-' }}</span>
                </div>
                <div class="ds-result__item">
                  <span class="ds-result__label">{{ $t('reserveConfirm.appointmentHour') }}</span>
                  <span class="ds-result__value">{{ reserveConfirmInfo.order.hours || '-' }}</span>
                </div>
                <div class="ds-result__item">
                  <span class="ds-result__label">{{ $t('reserveConfirm.appointmentPerson') }}</span>
                  <span class="ds-result__value">{{ reserveConfirmInfo.order.personName || '-' }}</span>
                </div>
                <div class="ds-result__item">
                  <span class="ds-result__label">{{ $t('reserveConfirm.appointmentPhone') }}</span>
                  <span class="ds-result__value">{{ reserveConfirmInfo.order.personTel || '-' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="17" :lg="18">
        <!-- (1) PageHeader -->
        <ds-page-header :title="$t('reserveConfirm.page.title')" :subtitle="$t('reserveConfirm.page.subtitle')" />

        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model="reserveConfirmInfo.conditions.personName" clearable
                :placeholder="$t('reserveConfirm.personNamePlaceholder')" @keyup.enter.native="queryReserveConfirm" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model="reserveConfirmInfo.conditions.personTel" clearable
                :placeholder="$t('reserveConfirm.personTelPlaceholder')" @keyup.enter.native="queryReserveConfirm" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="reserveConfirmInfo.conditions.appointmentTime" type="date"
                value-format="yyyy-MM-dd" :placeholder="$t('reserveConfirm.appointmentTimePlaceholder')"
                class="w-full" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="queryReserveConfirm">
              {{ $t('reserveConfirm.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetReserveConfirm">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="fetchData">
            <template #left>
              <span class="ds-panel__title">{{ $t('reserveConfirm.list.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="reserveConfirmInfo.orders" :loading="loading" :error="hasError"
            :error-message="errorMessage" :density="density" border
            :empty-description="$t('reserveConfirm.page.empty')" @retry="fetchData">
            <el-table-column prop="orderId" :label="$t('reserveConfirm.orderId')" align="center" width="180">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.orderId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="goodsName" :label="$t('reserveConfirm.goodsService')" align="left" min-width="150"
              show-overflow-tooltip />
            <el-table-column prop="quantity" :label="$t('reserveConfirm.quantity')" align="right" width="100">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatNumber(scope.row.quantity, 0) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="appointmentTime" :label="$t('reserveConfirm.appointmentDate')" align="center"
              width="120" />
            <el-table-column prop="hours" :label="$t('reserveConfirm.appointmentHour')" align="center" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.hours || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="personName" :label="$t('reserveConfirm.appointmentPerson')" align="center"
              width="100" />
            <el-table-column prop="personTel" :label="$t('reserveConfirm.appointmentPhone')" align="center"
              width="130" />
            <el-table-column prop="createTime" :label="$t('reserveConfirm.confirmTime')" align="center" width="160" />
            <el-table-column :label="$t('reserveConfirm.remark')" align="left" min-width="140" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.remark || '-' }}
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { listReserveGoodsConfirmOrder, saveReserveGoodsConfirmOrder } from '@/api/scm/reserveConfirmApi'
import { getCommunityId } from '@/api/community/communityApi'
import { formatNumber } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ReserveConfirmList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      reserveConfirmInfo: {
        orders: [],
        order: {
          remark: '',
          appointmentTime: '',
          createTime: '',
          hours: '',
          spaceName: '',
          personName: '',
          personTel: '',
          quantity: '',
          goodsName: ''
        },
        timeId: '',
        conditions: {
          spaceId: '',
          personName: '',
          personTel: '',
          appointmentTime: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  computed: {
    /**
     * 是否已有核销结果（仅控制结果区展示态，不影响任何业务逻辑）
     */
    hasConfirmResult() {
      const o = this.reserveConfirmInfo.order
      return !!(o.remark || o.createTime || o.goodsName || o.personName)
    }
  },
  created() {
    this.reserveConfirmInfo.conditions.communityId = getCommunityId()
    this.fetchData()
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.reserveConfirmInfo.conditions,
          page: this.page.current,
          row: this.page.size
        }
        const res = await listReserveGoodsConfirmOrder(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况，
        // 原实现直接取 data 会在此处抛 TypeError，导致筛选后表格仍停留在上一次的数据。
        this.reserveConfirmInfo.orders = Array.isArray(res.data) ? res.data : []
        this.page.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('reserveConfirm.page.loadFailed')
        console.error('Failed to load reserve confirm list:', error)
      } finally {
        this.loading = false
      }
    },
    async confirmReserve() {
      if (!this.reserveConfirmInfo.timeId) {
        this.$message.warning(this.$t('reserveConfirm.scanWarning'))
        return
      }

      try {
        // 扫码协议：ORDER:<支付单号> 整单批量核销；TIME:<timeId> 或裸 timeId 单条核销（兼容老码）
        const data = this._buildReserveConfirmParam(
          this.reserveConfirmInfo.timeId,
          this.reserveConfirmInfo.conditions.communityId
        )
        const res = await saveReserveGoodsConfirmOrder(data)

        if (res.code !== 0) {
          this.$message.error(res.msg)
          return
        }

        this.$message.success(this.$t('common.operationSuccess'))
        this.reserveConfirmInfo.timeId = ''
        this.fetchData()

        if (res.data && res.data.length > 0) {
          this.reserveConfirmInfo.order = { ...res.data[0] }
          if (!this.reserveConfirmInfo.order.remark) {
            this.reserveConfirmInfo.order.remark = this.$t('reserveConfirm.confirmSuccess')
          }
        }
      } catch (error) {
        this.$message.error(error.message)
        this.reserveConfirmInfo.timeId = ''
      }
    },
    queryReserveConfirm() {
      this.page.current = 1
      this.fetchData()
    },
    /**
     * 重置筛选：仅清空可筛选字段，保留 communityId。
     */
    resetReserveConfirm() {
      this.reserveConfirmInfo.conditions.personName = ''
      this.reserveConfirmInfo.conditions.personTel = ''
      this.reserveConfirmInfo.conditions.appointmentTime = ''
      this.queryReserveConfirm()
    },
    // 解析扫码/手工输入内容，返回核销接口参数
    // ORDER:<支付单号> -> { extOrderId } 整单批量；TIME:<timeId>/裸timeId -> { timeId } 单条（兼容老码）
    _buildReserveConfirmParam(rawCode, communityId) {
      const code = (rawCode || '').trim()
      if (/^ORDER:/i.test(code)) {
        return {
          extOrderId: code.replace(/^ORDER:/i, '').trim(),
          communityId: communityId
        }
      }
      return {
        timeId: code.replace(/^TIME:/i, '').trim(),
        communityId: communityId
      }
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.fetchData()
    },
    /**
     * 数量格式化（只改展示，不改原值）
     */
    formatNumber(value, decimals = 2) {
      return formatNumber(value, decimals)
    }
  }
}
</script>

<style lang="scss" scoped>
.reserve-confirm-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧核销工作台 */
.ds-confirm-card {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-sm);
    padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    background: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__body {
    padding: var(--ds-spacing-lg);
  }

  &__tip {
    display: flex;
    align-items: flex-start;
    gap: var(--ds-spacing-xs);
    margin-top: var(--ds-spacing-sm);
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
    line-height: 1.6;
  }
}

/* 核销结果区 */
.ds-result {
  margin-top: var(--ds-spacing-lg);
  padding-top: var(--ds-spacing-md);
  border-top: 1px dashed var(--ds-gray-300);

  &.is-empty {
    opacity: 0.6;
  }

  &__title {
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    margin-bottom: var(--ds-spacing-sm);
  }

  &__item {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--ds-spacing-md);
    padding: var(--ds-spacing-xs) 0;
    border-bottom: 1px solid var(--ds-gray-100);
    font-size: var(--ds-font-size-sm);
  }

  &__label {
    flex-shrink: 0;
    color: var(--ds-gray-500);
  }

  &__value {
    color: var(--ds-gray-800);
    text-align: right;
    word-break: break-all;
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
